import { API_BASE_URL } from '../config';

/**
 * SmartSign API client.
 *
 * Same conventions as service/classic.js: plain fetch, monday session token in
 * the URL path, JSON in/out. Endpoints: apr-backend src/routes/monday.ts.
 *
 * Two families, mirroring the backend's split:
 *   - ENVELOPES — the signing requests you build and send (tab 1).
 *   - TEMPLATES — the reusable starting points envelopes copy from.
 */

const json = (method, body) => ({
  method,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body),
});

const tok = (sessionToken) => encodeURIComponent(sessionToken);

// ── envelopes: the builder ───────────────────────────────────────────────────

/**
 * What is open on this item: `{ draft }`, `{ inFlight }`, or neither.
 *
 * An item carries one non-terminal request at a time, so tab 1 asks what state
 * the item is in rather than listing drafts.
 */
const getOpenRequest = async (sessionToken, itemId) => {
  const res = await fetch(`${API_BASE_URL}/sign/envelope-open/${itemId}/${tok(sessionToken)}`);
  return await res.json();
};

const createEnvelope = async (sessionToken, boardId, itemId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelopes/${boardId}/${tok(sessionToken)}`,
    json('POST', { itemId }),
  );
  return await res.json();
};

const getEnvelope = async (sessionToken, requestId) => {
  const res = await fetch(`${API_BASE_URL}/sign/envelope/${requestId}/${tok(sessionToken)}`);
  return await res.json();
};

/** Autosaved: name, signers, signingOrder, sending, output. */
const updateEnvelope = async (sessionToken, requestId, patch) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope/${requestId}/${tok(sessionToken)}`,
    json('PUT', patch),
  );
  return await res.json();
};

const saveEnvelopeFields = async (sessionToken, requestId, { prefillFields, signerFields }) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-fields/${requestId}/${tok(sessionToken)}`,
    json('PUT', { prefillFields, signerFields }),
  );
  return await res.json();
};

const deleteEnvelope = async (sessionToken, requestId) => {
  const res = await fetch(`${API_BASE_URL}/sign/envelope/${requestId}/${tok(sessionToken)}`, {
    method: 'DELETE',
  });
  return await res.json();
};

/** Upload a PDF into the envelope (multipart — no Content-Type header). */
const uploadEnvelopeDocument = async (sessionToken, requestId, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}/sign/envelope-document/${requestId}/${tok(sessionToken)}`, {
    method: 'POST',
    body: form,
  });
  return await res.json();
};

/** Copy a template's published version in — documents, fields and roles. */
const addEnvelopeDocumentFromTemplate = async (sessionToken, requestId, templateId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-document-from-template/${requestId}/${tok(sessionToken)}`,
    json('POST', { templateId }),
  );
  return await res.json();
};

/** Brings across EVERY PDF in one File column — the server re-reads the column. */
const addEnvelopeDocumentFromColumn = async (sessionToken, requestId, columnId, columnTitle) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-document-from-column/${requestId}/${tok(sessionToken)}`,
    json('POST', { columnId, columnTitle }),
  );
  return await res.json();
};

const removeEnvelopeDocument = async (sessionToken, requestId, documentId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-document/${requestId}/${documentId}/${tok(sessionToken)}`,
    { method: 'DELETE' },
  );
  return await res.json();
};

const reorderEnvelopeDocuments = async (sessionToken, requestId, order) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-documents-order/${requestId}/${tok(sessionToken)}`,
    json('PUT', { order }),
  );
  return await res.json();
};

/** Rename a document — display name only, bytes untouched. */
const renameEnvelopeDocument = async (sessionToken, requestId, documentId, name) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/envelope-document-name/${requestId}/${documentId}/${tok(sessionToken)}`,
    json('PUT', { name }),
  );
  return await res.json();
};

/** Authorised stream URL for the mapping canvas. */
const getEnvelopeDocumentUrl = (sessionToken, documentId) =>
  `${API_BASE_URL}/sign/envelope-document-file/${documentId}/${tok(sessionToken)}`;

/** Resolve email columns against this item, for prefilling a signer. */
const resolveItemEmails = async (sessionToken, itemId, columnIds) => {
  const qs = `?columns=${encodeURIComponent((columnIds || []).join(','))}`;
  const res = await fetch(`${API_BASE_URL}/sign/item-emails/${itemId}/${tok(sessionToken)}${qs}`);
  return await res.json();
};

/** Validate, freeze, stamp every document, invite. */
const sendEnvelope = async (sessionToken, requestId) => {
  const res = await fetch(`${API_BASE_URL}/sign/envelope-send/${requestId}/${tok(sessionToken)}`, {
    method: 'POST',
  });
  return await res.json();
};

// ── templates: the reusable starting points ──────────────────────────────────

const listTemplates = async (sessionToken, boardId) => {
  const res = await fetch(`${API_BASE_URL}/sign/templates/${boardId}/${tok(sessionToken)}`);
  return await res.json();
};

const createTemplate = async (sessionToken, boardId, name) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/templates/${boardId}/${tok(sessionToken)}`,
    json('POST', { name }),
  );
  return await res.json();
};

const getTemplate = async (sessionToken, templateId) => {
  const res = await fetch(`${API_BASE_URL}/sign/template/${templateId}/${tok(sessionToken)}`);
  return await res.json();
};

const updateTemplate = async (sessionToken, templateId, patch) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template/${templateId}/${tok(sessionToken)}`,
    json('PUT', patch),
  );
  return await res.json();
};

const deleteTemplate = async (sessionToken, templateId) => {
  const res = await fetch(`${API_BASE_URL}/sign/template/${templateId}/${tok(sessionToken)}`, {
    method: 'DELETE',
  });
  return await res.json();
};

const duplicateTemplate = async (sessionToken, templateId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-duplicate/${templateId}/${tok(sessionToken)}`,
    { method: 'POST' },
  );
  return await res.json();
};

const saveTemplateFields = async (sessionToken, templateId, { prefillFields, signerFields }) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-fields/${templateId}/${tok(sessionToken)}`,
    json('PUT', { prefillFields, signerFields }),
  );
  return await res.json();
};

const uploadTemplateDocument = async (sessionToken, templateId, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}/sign/template-document/${templateId}/${tok(sessionToken)}`, {
    method: 'POST',
    body: form,
  });
  return await res.json();
};

/** As above. A template has no item of its own, so the caller supplies one. */
const addTemplateDocumentFromColumn = async (sessionToken, templateId, columnId, columnTitle, itemId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-document-from-column/${templateId}/${tok(sessionToken)}`,
    json('POST', { columnId, columnTitle, itemId: String(itemId) }),
  );
  return await res.json();
};

const removeTemplateDocument = async (sessionToken, templateId, documentId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-document/${templateId}/${documentId}/${tok(sessionToken)}`,
    { method: 'DELETE' },
  );
  return await res.json();
};

const reorderTemplateDocuments = async (sessionToken, templateId, order) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-documents-order/${templateId}/${tok(sessionToken)}`,
    json('PUT', { order }),
  );
  return await res.json();
};

const renameTemplateDocument = async (sessionToken, templateId, documentId, name) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-document-name/${templateId}/${documentId}/${tok(sessionToken)}`,
    json('PUT', { name }),
  );
  return await res.json();
};

const getTemplateDocumentUrl = (sessionToken, documentId) =>
  `${API_BASE_URL}/sign/template-document-file/${documentId}/${tok(sessionToken)}`;

/** Write the immutable snapshot envelopes copy from. */
const publishTemplate = async (sessionToken, templateId) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/template-publish/${templateId}/${tok(sessionToken)}`,
    { method: 'POST' },
  );
  return await res.json();
};

const getBoardColumns = async (sessionToken, boardId) => {
  const res = await fetch(`${API_BASE_URL}/sign/board-columns/${boardId}/${tok(sessionToken)}`);
  return await res.json();
};

// ── tracking (tab 2) ────────────────────────────────────────────────────────

/** Sent envelopes for this item. Drafts belong to tab 1 and are excluded. */
const listSignRequests = async (sessionToken, itemId) => {
  const res = await fetch(`${API_BASE_URL}/sign/requests/${itemId}/${tok(sessionToken)}`);
  return await res.json();
};

const cancelSignRequest = async (sessionToken, requestId) => {
  const res = await fetch(`${API_BASE_URL}/sign/request-cancel/${requestId}/${tok(sessionToken)}`, {
    method: 'POST',
  });
  return await res.json();
};

/** The envelope's audit trail, now carrying a documentId per event. */
const getSignRequestEvents = async (sessionToken, requestId) => {
  const res = await fetch(`${API_BASE_URL}/sign/request-events/${requestId}/${tok(sessionToken)}`);
  return await res.json();
};

/** Download URL for ONE signed document. */
const getSignedPdfUrl = (sessionToken, requestId, documentId) =>
  `${API_BASE_URL}/sign/request-file/${requestId}/${documentId}/${tok(sessionToken)}`;

// ── board settings (the Settings tab) ───────────────────────────────────────

/**
 * `{ ok, settings }` — always resolves, even for a board that has never been
 * configured: the server returns its documented defaults without writing a row.
 */
const getBoardSettings = async (sessionToken, boardId) => {
  const res = await fetch(`${API_BASE_URL}/sign/board-settings/${boardId}/${tok(sessionToken)}`);
  return res.json();
};

/**
 * WHOLE-OBJECT replace — always send every group.
 *
 * A partial payload resets what it omits to that field's default, which for
 * `requireOtp` means turning identity verification on rather than off, but for
 * `fileColumnId` means clearing the board's destination. The Settings tab holds
 * all five sections on screen at once, so it always has the whole object.
 */
const saveBoardSettings = async (sessionToken, boardId, settings) => {
  const res = await fetch(
    `${API_BASE_URL}/sign/board-settings/${boardId}/${tok(sessionToken)}`,
    json('PUT', settings),
  );
  return res.json();
};

// ── sender identities (the board's, shared by everyone on it) ───────────────
//
// These return only addresses the session's own user verified. Another user on
// the same account has their own, and neither can see the other's — which is
// why remove and resend address one row by id and the server re-checks that it
// belongs to the caller.

/** `{ ok, senders: [{ id, email, displayName, status, verifiedAt, createdByUserId }] }` */
const getSenders = async (sessionToken, boardId) => {
  const res = await fetch(`${API_BASE_URL}/sign/sender/${boardId}/${tok(sessionToken)}`);
  return await res.json();
};

/** Adds an address, or renames one already yours. Never replaces another. */
const saveSender = async (sessionToken, boardId, email, displayName) => {
  const res = await fetch(`${API_BASE_URL}/sign/sender/${boardId}/${tok(sessionToken)}`, json('POST', { email, displayName }));
  return await res.json();
};

const removeSender = async (sessionToken, boardId, id) => {
  const res = await fetch(`${API_BASE_URL}/sign/sender/${boardId}/${id}/${tok(sessionToken)}`, { method: 'DELETE' });
  return await res.json();
};

const resendSenderVerification = async (sessionToken, boardId, id) => {
  const res = await fetch(`${API_BASE_URL}/sign/sender-resend/${boardId}/${id}/${tok(sessionToken)}`, { method: 'POST' });
  return await res.json();
};

export {
  // envelopes
  getOpenRequest,
  createEnvelope,
  getEnvelope,
  updateEnvelope,
  saveEnvelopeFields,
  deleteEnvelope,
  uploadEnvelopeDocument,
  addEnvelopeDocumentFromTemplate,
  addEnvelopeDocumentFromColumn,
  removeEnvelopeDocument,
  reorderEnvelopeDocuments,
  renameEnvelopeDocument,
  getEnvelopeDocumentUrl,
  resolveItemEmails,
  sendEnvelope,
  // templates
  listTemplates,
  createTemplate,
  getTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  saveTemplateFields,
  uploadTemplateDocument,
  addTemplateDocumentFromColumn,
  removeTemplateDocument,
  reorderTemplateDocuments,
  renameTemplateDocument,
  getTemplateDocumentUrl,
  publishTemplate,
  getBoardColumns,
  // tracking
  listSignRequests,
  cancelSignRequest,
  getSignRequestEvents,
  getSignedPdfUrl,
  // board settings
  getBoardSettings,
  saveBoardSettings,
  // sender
  getSenders,
  saveSender,
  removeSender,
  resendSenderVerification,
};
